export type IceBlock = {
    x: number
    y: number
    targetY: number // Final Y position (visual top)
    baseY: number   // Where it grows from
    width: number
    height: number
    targetHeight: number
    growthSpeed: number
    color: string
    delay: number
    active: boolean
    shatterSpeedX: number
    shatterSpeedY: number
    rotation: number
    shatterRotationSpeed: number
    alpha: number
    points: { x: number, y: number }[] // Relative polygon points
}

export type GlossParticle = {
    x: number
    y: number
    vx: number
    vy: number
    life: number
    maxLife: number
    size: number
    color: string
}

export type HibernationData = {
    blocks: IceBlock[]
    particles: GlossParticle[]
}

export type HibernationEffect = {
    kind: 'hibernation'
    t: number
    x: number
    y: number
    w: number
    h: number
    data: HibernationData
}

/**
 * Initializes and spawns a Hibernation effect
 */
export function createHibernationEffect(x: number, y: number, w: number, h: number): HibernationEffect {
    const blocks: IceBlock[] = []
    const particles: GlossParticle[] = []

    const cols = 7
    const colWidth = w / cols
    const midX = x + w / 2

    // Helper to generate jagged crystal points
    const generateCrystalPoints = (w: number, h: number) => {
        const pts = []
        // Bottom Left
        pts.push({ x: -w / 2, y: h })
        // Left Side (Jagged)
        pts.push({ x: -w / 2 - Math.random() * w * 0.2, y: h * 0.6 })
        pts.push({ x: -w / 2 + Math.random() * w * 0.1, y: h * 0.3 })
        // Top Tip
        pts.push({ x: (Math.random() - 0.5) * w * 0.5, y: 0 })
        // Right Side (Jagged)
        pts.push({ x: w / 2 - Math.random() * w * 0.1, y: h * 0.3 })
        pts.push({ x: w / 2 + Math.random() * w * 0.2, y: h * 0.6 })
        // Bottom Right
        pts.push({ x: w / 2, y: h })
        return pts
    }

    // 1. Base Layer (Background)
    for (let i = 0; i < cols; i++) {
        const centerX = x + colWidth * i + colWidth * 0.5
        const bx = centerX + (Math.random() - 0.5) * 15

        // Bow shape logic:
        // Calculate distance from center (0 to 1)
        const distNorm = Math.abs(bx - midX) / (w / 2)
        // Middle (0) -> Lower (Higher Y)
        // Edges (1) -> Upper (Lower Y)
        // Offset max ~20% of height
        const yOffset = distNorm * (h * 0.25)
        const startY = (y + h) - yOffset

        // Increased Size: +30% roughly
        const targetH = h * (0.3 + Math.random() * 1.1) // Was 0.2-1.1 -> Now 0.3-1.4
        const width = colWidth * (0.7 + Math.random() * 1.2) // Was 0.5-1.5 -> Now 0.7-1.9

        blocks.push({
            x: bx,
            y: startY,
            baseY: startY,
            targetY: startY - targetH,
            width: width,
            height: 0,
            targetHeight: targetH,
            growthSpeed: 8 + Math.random() * 12,
            color: `rgba(249, 168, 212, ${0.3 + Math.random() * 0.2})`,
            delay: Math.random() * 300,
            active: false,
            shatterSpeedX: (Math.random() - 0.5) * 8,
            shatterSpeedY: -2 - Math.random() * 5,
            rotation: 0,
            shatterRotationSpeed: (Math.random() - 0.5) * 0.1,
            alpha: 0,
            points: generateCrystalPoints(width, targetH)
        })
    }

    // 2. Foreground Layer (More prominent)
    for (let i = 0; i < cols - 1; i++) {
        const centerX = x + colWidth * i + colWidth
        const bx = centerX + (Math.random() - 0.5) * 20

        const distNorm = Math.abs(bx - midX) / (w / 2)
        const yOffset = distNorm * (h * 0.25)
        const startY = (y + h) - yOffset

        // Increased Size
        const targetH = h * (0.4 + Math.random() * 1.0) // Was 0.3-1.1 -> Now 0.4-1.4
        const width = colWidth * (0.8 + Math.random() * 1.4) // Was 0.6-1.8 -> Now 0.8-2.2

        blocks.push({
            x: bx,
            y: startY,
            baseY: startY,
            targetY: startY - targetH,
            width: width,
            height: 0,
            targetHeight: targetH,
            growthSpeed: 10 + Math.random() * 15,
            color: `rgba(244, 114, 182, ${0.3 + Math.random() * 0.2})`,
            delay: 150 + Math.random() * 300,
            active: false,
            shatterSpeedX: (Math.random() - 0.5) * 12,
            shatterSpeedY: -5 - Math.random() * 8,
            rotation: 0,
            shatterRotationSpeed: (Math.random() - 0.5) * 0.2,
            alpha: 0,
            points: generateCrystalPoints(width, targetH)
        })
    }

    // 3. Extra "Wild" Crystals
    for (let i = 0; i < 3; i++) {
        const bx = x + Math.random() * w

        const distNorm = Math.abs(bx - midX) / (w / 2)
        const yOffset = distNorm * (h * 0.25)
        const startY = (y + h) - yOffset

        const isHuge = Math.random() > 0.5
        // Huge ones even bigger
        const targetH = isHuge ? h * (1.0 + Math.random() * 0.5) : h * (0.15 + Math.random() * 0.25)
        const width = isHuge ? colWidth * (1.8 + Math.random() * 1.2) : colWidth * (0.4 + Math.random() * 0.4)

        blocks.push({
            x: bx,
            y: startY,
            baseY: startY,
            targetY: startY - targetH,
            width: width,
            height: 0,
            targetHeight: targetH,
            growthSpeed: 12 + Math.random() * 10,
            color: isHuge ? `rgba(236, 72, 153, ${0.2 + Math.random() * 0.2})` : `rgba(253, 224, 71, ${0.4 + Math.random() * 0.2})`,
            delay: Math.random() * 400,
            active: false,
            shatterSpeedX: (Math.random() - 0.5) * 15,
            shatterSpeedY: -5 - Math.random() * 10,
            rotation: 0,
            shatterRotationSpeed: (Math.random() - 0.5) * 0.3,
            alpha: 0,
            points: generateCrystalPoints(width, targetH)
        })
    }

    return {
        kind: 'hibernation',
        t: 0,
        x, y, w, h,
        data: { blocks, particles }
    }
}

export function renderHibernationEffect(
    effect: HibernationEffect,
    ctx: CanvasRenderingContext2D
): boolean {
    const maxTime = 4000
    const tt = effect.t / maxTime
    if (tt > 1.0) return false

    const data = effect.data
    const isShattering = tt > 0.8

    // Spawn Gloss Particles
    if (!isShattering && effect.t % 2 < 1) {
        const activeBlocks = data.blocks.filter(b => b.active && b.height > 10)
        if (activeBlocks.length > 0) {
            const b = activeBlocks[Math.floor(Math.random() * activeBlocks.length)]
            const px = b.x + (Math.random() - 0.5) * b.width
            const py = b.baseY - Math.random() * b.height

            data.particles.push({
                x: px,
                y: py,
                vx: (Math.random() - 0.5) * 0.5,
                vy: -0.2 - Math.random() * 0.5,
                life: 0,
                maxLife: 40 + Math.random() * 40,
                size: 1 + Math.random() * 3,
                color: Math.random() > 0.5 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(249, 168, 212, 0.8)'
            })
        }
    }

    ctx.save()

    // Render Blocks
    data.blocks.forEach(b => {
        if (!b.active && effect.t > b.delay) {
            b.active = true
            b.alpha = 0
        }

        if (b.active) {
            if (!isShattering) {
                if (b.height < b.targetHeight) {
                    b.height += b.growthSpeed
                } else {
                    b.height = b.targetHeight
                }
                if (b.alpha < 1) b.alpha += 0.05
            } else {
                b.x += b.shatterSpeedX
                b.y += b.shatterSpeedY
                b.rotation += b.shatterRotationSpeed
                b.alpha -= 0.05
                b.shatterSpeedY += 0.5
            }

            if (b.alpha > 0) {
                ctx.save()
                ctx.globalAlpha = b.alpha

                ctx.translate(b.x, b.baseY)
                ctx.rotate(b.rotation)

                const scaleY = b.height / b.targetHeight
                ctx.scale(1, scaleY)

                ctx.beginPath()
                b.points.forEach((p, i) => {
                    const py = p.y - b.targetHeight
                    if (i === 0) ctx.moveTo(p.x, py)
                    else ctx.lineTo(p.x, py)
                })
                ctx.closePath()

                const grad = ctx.createLinearGradient(0, -b.targetHeight, 0, 0)
                grad.addColorStop(0, 'rgba(255, 255, 255, 0.95)')
                grad.addColorStop(0.4, b.color)
                grad.addColorStop(1, 'rgba(255, 255, 255, 0.2)')

                ctx.fillStyle = grad
                ctx.fill()

                ctx.beginPath()
                ctx.moveTo(0, -b.targetHeight)
                ctx.lineTo(b.width * 0.2, -b.targetHeight * 0.5)
                ctx.lineTo(0, 0)
                ctx.fillStyle = 'rgba(255, 255, 255, 0.3)'
                ctx.fill()

                ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)'
                ctx.lineWidth = 1
                ctx.stroke()

                ctx.restore()
            }
        }
    })

    // Render Particles
    ctx.globalCompositeOperation = 'screen'
    for (let i = data.particles.length - 1; i >= 0; i--) {
        const p = data.particles[i]
        p.life++
        p.x += p.vx
        p.y += p.vy

        if (p.life >= p.maxLife) {
            data.particles[i] = data.particles[data.particles.length - 1]
            data.particles.pop()
            continue
        }

        const alpha = Math.sin((p.life / p.maxLife) * Math.PI) * 0.8

        ctx.save()
        ctx.globalAlpha = alpha
        ctx.translate(p.x, p.y)

        const size = p.size
        ctx.beginPath()
        ctx.moveTo(0, -size)
        ctx.quadraticCurveTo(size * 0.1, -size * 0.1, size, 0)
        ctx.quadraticCurveTo(size * 0.1, size * 0.1, 0, size)
        ctx.quadraticCurveTo(-size * 0.1, size * 0.1, -size, 0)
        ctx.quadraticCurveTo(-size * 0.1, -size * 0.1, 0, -size)

        ctx.fillStyle = p.color
        ctx.fill()

        ctx.beginPath()
        ctx.arc(0, 0, size * 0.3, 0, Math.PI * 2)
        ctx.fillStyle = 'white'
        ctx.fill()

        ctx.restore()
    }

    ctx.restore()

    return true
}
